/**
 * Agency → one athlete: profile summary, their deals with commission,
 * and the agency's commission rate for them.
 *
 * Deals created by the agent land as DRAFTs the athlete must confirm
 * (D9), so this page distinguishes "Awaiting confirmation" from a normal
 * draft — the agent needs to know which of their proposals are still
 * sitting unacknowledged.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from '@app/ui';
import { formatCurrency, formatDate } from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../../../../lib/api';

export const Route = createFileRoute('/_authenticated/agency/athletes/$athleteId/')({
  loader: async ({ context, params }) => {
    const { getServerApi } = await import('../../../../../lib/api-server');
    const serverApi = await getServerApi();
    const { agencies } = await context.queryClient.ensureQueryData(
      serverApi.agency.myAgencies.queryOptions(),
    );
    const orgId = agencies[0]?.id;
    if (orgId) {
      await context.queryClient.ensureQueryData(
        serverApi.agency.getAthlete.queryOptions({
          input: { orgId, athleteId: params.athleteId },
        }),
      );
    }
  },
  errorComponent: ({ error }) => (
    <div className="max-w-2xl">
      <Link to="/agency/roster" className="text-sm text-muted-foreground hover:underline">
        ← Back to roster
      </Link>
      <Card className="mt-4">
        <CardContent className="py-12 text-center">
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load athlete'}
          </p>
        </CardContent>
      </Card>
    </div>
  ),
  component: AgencyAthletePage,
});

function AgencyAthletePage() {
  const { athleteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: mine } = useSuspenseQuery(api.agency.myAgencies.queryOptions());
  const orgId = mine.agencies[0].id;

  const { data } = useSuspenseQuery(
    api.agency.getAthlete.queryOptions({ input: { orgId, athleteId } }),
  );

  const [rate, setRate] = useState(
    data.memberCommissionPercent === null ? '' : String(data.memberCommissionPercent),
  );
  const [contractStart, setContractStart] = useState(
    data.memberContractStartDate ? data.memberContractStartDate.slice(0, 10) : '',
  );

  const rateMutation = useMutation({
    ...api.agency.setAthleteTerms.mutationOptions(),
    onSuccess: () => {
      toast.success('Terms saved');
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
      });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  });

  const handleSaveTerms = () => {
    const trimmed = rate.trim();
    let commissionPercent: number | null = null;
    if (trimmed !== '') {
      const n = Number(trimmed);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        toast.error('Commission must be between 0 and 100');
        return;
      }
      commissionPercent = n;
    }
    rateMutation.mutate({
      orgId,
      athleteId,
      commissionPercent,
      contractStartDate: contractStart.trim() === '' ? null : contractStart,
    });
  };

  const awaiting = data.deals.filter((d) => d.awaitingConfirmation);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link to="/agency/roster" className="text-sm text-muted-foreground hover:underline">
          ← Back to roster
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap mt-2">
          <div>
            <h1 className="text-2xl font-bold">{data.athlete.name || data.athlete.email}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {[data.athlete.sport, data.athlete.school, data.athlete.classYear]
                .filter(Boolean)
                .join(' · ') || data.athlete.email}
            </p>
          </div>
          <Link
            to="/agency/athletes/$athleteId/deals/new"
            params={{ athleteId }}
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Add a deal
          </Link>
        </div>
      </div>

      {data.athlete.status === 'invited' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-900">
            This athlete hasn't signed in yet. You can still add deals — they'll see them waiting
            when they accept the invite.
          </CardContent>
        </Card>
      )}

      {awaiting.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3 text-sm text-blue-900">
            {awaiting.length} deal{awaiting.length === 1 ? '' : 's'} you added{' '}
            {awaiting.length === 1 ? 'is' : 'are'} waiting on {data.athlete.name || 'the athlete'}{' '}
            to confirm. They stay drafts until then.
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label="Deals" value={String(data.totals.deals)} />
        <StatCard label="Tracked value" value={formatCurrency(data.totals.value)} />
        <StatCard label="Est. commission" value={formatCurrency(data.totals.commission)} />
      </div>

      {data.totals.excludedByContractWindow > 0 && (
        <p className="text-xs text-muted-foreground -mt-2">
          {data.totals.excludedByContractWindow} deal
          {data.totals.excludedByContractWindow === 1 ? '' : 's'} signed before your contract start
          {data.memberContractStartDate
            ? ` (${formatDate(data.memberContractStartDate)})`
            : ''}{' '}
          {data.totals.excludedByContractWindow === 1 ? 'is' : 'are'} shown below but excluded from
          these totals.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Representation terms</CardTitle>
          <CardDescription>
            Your default commission rate for this athlete, and when your representation began.
            Earnings are always calculated from these — nothing is stored, so changing them
            reprices consistently.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
            <div>
              <Label htmlFor="rate">Commission percent</Label>
              <Input
                id="rate"
                inputMode="decimal"
                placeholder="e.g. 15"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Blank = no commission.</p>
            </div>
            <div>
              <Label htmlFor="contract-start">Contract start date</Label>
              <Input
                id="contract-start"
                type="date"
                value={contractStart}
                onChange={(e) => setContractStart(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Blank = count every deal.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleSaveTerms} disabled={rateMutation.isPending}>
              {rateMutation.isPending ? 'Saving…' : 'Save terms'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Deals signed before the contract start still appear below for context, but don't count
            toward your tracked value or commission.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deals</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {data.deals.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No deals yet. Add one — it'll go to {data.athlete.name || 'them'} to confirm.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Deal</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-right font-medium">Commission</th>
                  <th className="px-3 py-2 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.map((d) => (
                  <tr
                    key={d.id}
                    className={`border-t ${d.inContractWindow ? '' : 'opacity-50'}`}
                  >
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium">{d.title}</div>
                      <div className="text-xs text-muted-foreground">{d.brandName}</div>
                      {!d.inContractWindow && (
                        <div className="text-xs text-muted-foreground italic mt-0.5">
                          Outside contract — not counted
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      {d.awaitingConfirmation ? (
                        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                          Awaiting confirmation
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs bg-muted text-muted-foreground">
                          {d.status}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-right">{formatCurrency(d.amount)}</td>
                    <td className="px-3 py-3 align-top text-right">
                      {d.commissionPercent === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <>
                          {formatCurrency(d.commissionEarned)}
                          <div className="text-xs text-muted-foreground">{d.commissionPercent}%</div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-xs text-muted-foreground">
                      {formatDate(d.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}
